import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBWaNoteThunks, waNoteSelectByQuery } from '@common-reducers/DBServiceThunks';
import { WaNote } from '@common-models/WaNote';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Box, Typography } from '@mui/material';
import WaNoteItem from './wa-note-item';
import { useTranslation } from 'react-i18next';
import { namespace } from "@common-config/constants";

type Props = {
    viewMode: 'current' | 'all';
    chatId?: string;
    emptyMessage?: string;
};

const NotesLazyLoadingVirtuoso = ({ viewMode, chatId, emptyMessage }: Props) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const hasFetchedRef = useRef<string | null>(null);

    // Build query based on view mode
    const query = useMemo(() => {
        if (viewMode === 'all') {
            return { deleted: false };
        }
        return chatId ? { chatId, deleted: false } : null;
    }, [viewMode, chatId]);

    // Create a stable query key for tracking fetches
    const queryKey = useMemo(() => {
        return JSON.stringify(query);
    }, [query]);

    const isItemsLoaded = useAppSelector(state => state.DBWaNoteReducer?.itemsLoaded);

    // Select notes based on view mode
    const notes = useAppSelector((state) => {
        if (!query) return [];
        return waNoteSelectByQuery(state.DBWaNoteReducer, query, [], { createdAt: -1 });
    });

    // Sort notes by date desc and filter deleted
    const sortedNotes = useMemo(() => {
        return [...notes]
            .filter(note => !note.deleted)
            .sort((a, b) => {
                return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
            });
    }, [notes]);

    // Load more notes for pagination
    const loadMore = useCallback(() => {
        if (!query) return;

        const skipCount = sortedNotes.length;
        if (skipCount === 0) return; // Initial fetch handles this

        dispatch(DBWaNoteThunks.find({
            ...query,
            $sort: { createdAt: -1 },
            $skip: skipCount,
            $limit: 50,
            useCache: true,
        }));
    }, [query, sortedNotes.length, dispatch]);

    // Initial fetch when query changes
    useEffect(() => {
        if (!query) return;

        // Only fetch if we haven't fetched this query yet
        if (hasFetchedRef.current === queryKey) return;
        hasFetchedRef.current = queryKey;

        dispatch(DBWaNoteThunks.find({
            ...query,
            $sort: { createdAt: -1 },
            $limit: 50,
            useCache: true,
        }));
    }, [query, queryKey, dispatch]);

    // Render note item
    const renderNoteItem = useCallback((index: number, note: WaNote) => {
        return (
            <div style={{ padding: '0 16px' }}>
                <WaNoteItem
                    key={note._id}
                    item={note}
                    showContactName={viewMode === 'all'}
                    currentChatId={chatId}
                />
            </div>
        );
    }, [viewMode, chatId]);

    // Loading state
    if (!isItemsLoaded) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                    {t('notes.loadingNotes')}
                </Typography>
            </Box>
        );
    }

    // Empty state
    if (sortedNotes.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="textSecondary" align="center">
                    {emptyMessage || t('notes.noNotesYet')}
                </Typography>
            </Box>
        );
    }

    // Notes list with virtualization
    return (
        <Virtuoso
            style={{ height: '100%' }}
            data={sortedNotes}
            endReached={loadMore}
            increaseViewportBy={200}
            itemContent={renderNoteItem}
            components={{
                Header: () => <div style={{ height: 16 }} />,
                Footer: () => <div style={{ height: 16 }} />,
            }}
        />
    );
};

export default NotesLazyLoadingVirtuoso;
